import { Box } from '@factorialco/deprecated-design-system'

import Filter from 'modules/core/components/Filter'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ContractSummary from 'modules/employees/pages/BulkFixContractsPage/BulkFixWizard/ContractSummary'
import { setLegalEntityAction, setPolicyAction } from '../actions'
import { NewCompensationStep, StepComponent, StepValidator } from '../types'

const ScopingStep: StepComponent = ({ data, onUpdate }) => {
  const {
    targetEmployees,
    allowedLegalEntities,
    allowedPayrollPolicies,
    affectedEmployeeIds,
    unaffectedEmployeeIds,
    legalEntityId,
    policyId,
  } = data
  const { company, access } = useCurrentSession()

  /* ===== State helpers ===== */

  const legalEntityOptions = useUnsafeMemo(
    () =>
      allowedLegalEntities.map((le) => ({
        label: le.name,
        value: le.id,
      })),
    [allowedLegalEntities]
  )

  const payrollPolicyOptions = useUnsafeMemo(
    () =>
      allowedPayrollPolicies
        .map((le) => ({
          label: le.name,
          value: le.id,
        }))
        // Make the 'No policy' option the last one, else use alpha sort
        .sort((a, b) => {
          if (a.value < 0) {
            return 1
          } else if (b.value < 0) {
            return -1
          }

          return a.label.localeCompare(b.label)
        }),
    [allowedPayrollPolicies]
  )

  /* ===== Event handlers ===== */

  const handleLegalEntityChange = useUnsafeCallback(
    (newLegalEntityId: number) => {
      if (!newLegalEntityId) return
      onUpdate(setLegalEntityAction(newLegalEntityId))
    },
    [onUpdate]
  )

  const handlePayrollPolicyChange = useUnsafeCallback(
    (newPolicyId: number) => {
      // Don't allow removing policy
      if (!newPolicyId) return
      onUpdate(setPolicyAction(newPolicyId))
    },
    [onUpdate]
  )

  useEffect(() => {
    track(trackingEvents.fixAgreementInBulk.fixAgreementSelectPolicy, {
      companyId: company.id,
      acessId: access.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { SingleChoice } = Filter

  const showSummary = legalEntityId !== undefined && policyId !== undefined

  return (
    <Box height="full" maxWidth="s600" gap="s24">
      <Box flexDirection="row" gap="s12">
        {legalEntityOptions.length > 1 && (
          <SingleChoice
            noFooter
            label={i18n.t('employees.bulk_edit.fix_conditions.steps.scope.entity_selector_label')}
            options={legalEntityOptions}
            value={legalEntityId}
            onValueChange={handleLegalEntityChange}
          />
        )}
        {payrollPolicyOptions.length > 1 && (
          <SingleChoice
            noFooter
            label={i18n.t('employees.bulk_edit.fix_conditions.steps.scope.policy_selector_label')}
            options={payrollPolicyOptions}
            value={policyId}
            onValueChange={handlePayrollPolicyChange}
          />
        )}
      </Box>

      {showSummary && (
        <ContractSummary
          affectedEmployeeIds={affectedEmployeeIds}
          unaffectedEmployeeIds={unaffectedEmployeeIds}
          targetEmployees={targetEmployees}
        />
      )}
    </Box>
  )
}

/* ===== Validators ===== */

const isCompletedStep: StepValidator = ({
  allowedLegalEntities,
  allowedPayrollPolicies,
  legalEntityId,
  policyId,
  affectedEmployeeIds,
}) => {
  return (
    !!affectedEmployeeIds.length &&
    !!allowedLegalEntities.length &&
    !!allowedPayrollPolicies.length &&
    legalEntityId !== undefined &&
    policyId !== undefined
  )
}

/* ===== Exports ===== */

const stepDefinition: NewCompensationStep = () => ({
  Component: ScopingStep,
  isCompleted: isCompletedStep,
  title: i18n.t('employees.bulk_edit.fix_conditions.steps.scope.title'),
})

export default stepDefinition
